import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import _ from 'lodash';
import { Form } from 'react-bootstrap';
import { layoutDefaults } from '../../utils/layout-defaults';
import { InputFilter } from '../input-filter';
import { NewAddressListItem } from './new-address-list-item';
import { ModalBodyDivider } from '../modal/modal-body-divider';
// import { useGoogleMapsGeocoder } from '../../hooks/use-google-maps-geocoder';
import { useGoogleMapsAutocompleteService } from '../../hooks/use-google-maps-autocomplete-service';
import { useGoogleMapsPlacesService } from '../../hooks/use-google-maps-places-service';

// const restrictToCountryTypes = false;
// const radius = 20000;

// https://mapsplatform.google.com/resources/blog/introducing-react-components-for-the-maps-javascript-api/
// https://www.pluralsight.com/resources/blog/guides/how-to-use-geolocation-call-in-reactjs

const types = [
  'premise',
  'subpremise',
  'route',
  'establishment',
  'street_address',
  'postal_code',
];

function NewAddressModalBody({
  defaultFilter,
  defaultData,
  storage,
  // onError,
  // onTimeout,
  onMessage,
  onSelect,
  onCurrentLocationClick,
  onFilterChange,
  onDataChange,
}) {
  const { menuInfo } = storage;
  const [filter, setFilter] = useState(defaultFilter);
  const [data, setData] = useState(defaultData ?? []);
  const [debounced, setDebounced] = useState();
  const [loadingSelection, setLoadingSelection] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const mapRef = useRef();

  const classes = useStyles();
  const { t } = useTranslation();

  const {
    isAutocompleteServiceLoaded,
    autocompleteService,
    autocompleteSessionToken,
  } = useGoogleMapsAutocompleteService();
  const { isPlacesServiceLoaded, placesService } = useGoogleMapsPlacesService(
    mapRef.current
  );
  // const { isGeocoderLoaded, geocoder } = useGoogleMapsGeocoder();

  // TODO: capturar os eventos com o analytics

  useEffect(() => {
    onFilterChange?.call(this, filter);
  }, [filter]);

  useEffect(() => {
    onDataChange?.call(this, data);
  }, [data]);

  useEffect(() => {
    // TODO: só ativar as buscas quando o componente estiver carregado
  }, [isAutocompleteServiceLoaded, isPlacesServiceLoaded]);

  useEffect(() => {
    if (!_.isEmpty(debounced) && _.isEmpty(data) && !loadingResults) {
      onMessage?.call(this, 'Nenhum endereço encontrado');
    } else {
      onMessage?.call(this, null);
    }
  }, [debounced, data, loadingResults]);

  const inputRef = useRef();

  useEffect(() => {
    if (!_.isEmpty(defaultFilter)) {
      _.delay(() => {
        // TODO: verificar porque não está conseguindo focar no input
        inputRef.current?.focus();
      }, layoutDefaults.keyboardDuration * 2);
    }
  }, [inputRef.current, defaultFilter]);

  // eslint-disable-next-line no-undef
  // const geolocation = useMemo(() => navigator.geolocation, []);

  const onChange = async (event) => {
    const lValue = event?.target?.value ?? '';
    setFilter(lValue);

    if (_.isEmpty(lValue)) {
      setData([]);
      setDebounced();
      setLoadingResults(false);
    } else {
      setLoadingResults(true);
    }
  };

  const handleOnSelectPlace = async (place, setLoading) => {
    setLoading(true);
    setLoadingSelection(true);

    _.delay(async () => {
      placesService?.getDetails(
        {
          placeId: place.place_id,
          fields: [
            'place_id',
            'name',
            'address_components',
            'geometry',
            'utc_offset_minutes',
            'types',
          ],
          sessionToken: autocompleteSessionToken,
        },
        async (placeDetails) => {
          await onSelect?.call(this, placeDetails, (loading) => {
            setLoading(loading);
            setLoadingSelection(loading);
          });
        }
      );
    }, layoutDefaults.keyboardDuration);
  };

  const handleOnSelectCurrentLocation = async (_place, setLoading) => {
    onMessage?.call(this, null);
    setLoading(true);
    setLoadingSelection(true);

    onCurrentLocationClick?.call(this, setLoading);
  };

  const onDebounce = async (pValue) => {
    if (_.isEmpty(pValue)) return;
    setDebounced(pValue);

    const country = menuInfo?.address?.country?.code;
    const coords = menuInfo?.address?.coordinate;

    // eslint-disable-next-line no-undef
    const location = new google.maps.LatLng(coords.latitude, coords.longitude);

    const result = await autocompleteService?.getPlacePredictions({
      input: pValue,
      locationBias: location,
      origin: location,
      componentRestrictions: {
        country,
      },
      sessionToken: autocompleteSessionToken,
    });

    let filtered = [];
    if (result.predictions?.length > 0) {
      filtered = result.predictions.filter(
        (p) => _.intersection(p.types, types).length > 0
      );
    }

    setData(filtered);
    setLoadingResults(false);
  };

  return (
    <div className={classes.container}>
      <div ref={mapRef} style={{ visibility: 'hidden' }} />
      <InputFilter
        inputRef={inputRef}
        placeholder={t('new-address-modal.search.placeholder')}
        loading={loadingResults}
        value={filter}
        disabled={loadingSelection}
        onChange={onChange}
        onDebounce={onDebounce}
        maxLength={500}
        dismissOnScroll={false}
      />

      {!_.isEmpty(data) && (
        <div className={classes.resultsContainer}>
          <Form>
            {data?.map((place, index) => (
              <NewAddressListItem
                key={place.place_id}
                place={place}
                index={index}
                disabled={loadingSelection}
                country={menuInfo?.address?.country}
                onSelect={handleOnSelectPlace}
              />
            ))}

            {!_.isEmpty(data) && (
              <NewAddressListItem
                key="gps"
                index={data.length}
                disabled={loadingSelection}
                country={menuInfo?.address?.country}
                onSelect={handleOnSelectCurrentLocation}
              />
            )}
          </Form>
        </div>
      )}

      {_.isEmpty(data) && (
        <div className={classes.orElseContainer}>
          <ModalBodyDivider
            title={t('divider.or')}
            className={classes.divider}
          />
          <NewAddressListItem
            key="gps"
            country={menuInfo?.address?.country}
            onSelect={handleOnSelectCurrentLocation}
          />
        </div>
      )}
    </div>
  );
}

const useStyles = createUseStyles({
  container: {
    marginTop: -layoutDefaults.smallVerticalMargin,
    marginBottom: layoutDefaults.smallVerticalMargin,
    // border: '1px solid white',
  },
  resultsContainer: {
    // minHeight: 200,
    // borderRadius: 100,
    // border: '1px solid red',
  },
  divider: {
    marginTop: layoutDefaults.smallerVerticalMargin,
    marginBottom: layoutDefaults.verticalMargin,
    opacity: layoutDefaults.opacity,
  },
  orElseContainer: {
    // marginBottom: layoutDefaults.verticalMargin,
  },
});

export { NewAddressModalBody };
